import React from "react";
import {isEmpty, keys, map} from "lodash";
import {get} from "lodash";
import {createUsersForOrganizations} from "../../../Services/api";
import {GridReactContext} from "../../../Utils/DataGridWrapper/GridContext";

/**
 * @typedef {object} UsersToUploadData
 * @property {Array<object>} users - An array of user objects parsed from the import file, ready for upload.
 * @property {Array<string>} organizationIds - An array of organization IDs to which the imported users will be assigned.
 * @property {object|Array<string>} [errors] - Optional validation errors encountered during parsing the import file.
 * @property {boolean} [isThereTwoSourcesForOrgs] - Optional flag indicating if the imported data contains conflicting sources for organization assignments.
 * @property {boolean} [isMissingSourceOfOrgs] - Optional flag indicating if required organization source data is missing.
 * @property {boolean} [sendWelcomeMessage] - Optional flag indicating whether welcome messages should be sent to new users.
 * @property {Array<string>} [selectedMessagingTypes] - Optional array of strings specifying the types of welcome messages to send.
 * @property {boolean} [onlyAddOrgsWithoutRemoving] - Optional flag indicating if only new organization assignments should be added without removing existing ones.
 */

/**
 * @property {boolean} canSubmit - Indicates whether the user import form can be submitted.
 * @property {UsersToUploadData} usersToUploadData - The data parsed from the import file, including users, organization IDs, and errors.
 * @property {boolean} isUploading - Indicates if an upload operation is currently in progress.
 * @property {boolean} isRebootUsers - Indicates if the current import operation is for "rebooting" users.
 * @property {function(): boolean} isSubmitDisabled - A function that returns true if the submit button should be disabled based on current state.
 * @property {function({isRebootUsers: boolean}): Promise<void>} triggerUploadUsers - A function to initiate the user upload process.
 * @property {function(boolean): void} setCanSubmitFunc - Function to update the `canSubmit` state.
 * @property {function(UsersToUploadData): void} setUsersToUploadDataFunc - Function to update the `usersToUploadData` state.
 * @property {function(boolean): void} setIsRebootUsersFunc - Function to update the `isRebootUsers` state.
 */

/**
 * React Context for managing user import functionality.
 * Provides state and functions related to user data upload, submission status, and upload progress.
 *
 * @type {React.Context}
 */
export const ImportUsersReactContext = React.createContext(({
    canSubmit: false,
    usersToUploadData: {
        users: [],
        organizationIds: [],
        errors: {},
        isThereTwoSourcesForOrgs: false,
        isMissingSourceOfOrgs: false,
        sendWelcomeMessage: false,
        selectedMessagingTypes: [],
        onlyAddOrgsWithoutRemoving: false,
    },
    isUploading: false,
    isRebootUsers: false,
    isSubmitDisabled: () => true, // Default to disabled
    triggerUploadUsers: async () => {}, // Default no-op function
    setCanSubmitFunc: (value) => {}, // Default no-op function
    setUsersToUploadDataFunc: (data) => {}, // Default no-op function
    setIsRebootUsersFunc: (value) => {}, // Default no-op function
}));


/**
 * Provides context for importing users, managing upload state, and triggering the upload process.
 * This context includes functions to update submission status, user data for upload, and
 * handles the actual API call for user creation.
 *
 * @param {object} params - The parameters for the component.
 * @param {React.ReactNode} params.children - The child components to be rendered within the provider.
 * @returns {JSX.Element} The context provider for import users functionality.
 */
export const ImportUserContextProvider = ({children}) => {
    const [canSubmit, setCanSubmit] = React.useState(false)
    const [usersToUploadData, setUsersToUploadData] = React.useState({})
    const [isUploading, setIsUploading] = React.useState(false)
    const gridContext = React.useContext(GridReactContext)
    const [isRebootUsers, setIsRebootUsers] = React.useState(false)

    /**
     * Sets the `canSubmit` state, indicating whether the user import form can be submitted.
     * @param {boolean} value - The boolean value to set `canSubmit` to.
     * @returns {void}
     */
    const setCanSubmitFunc = (value) => {
        setCanSubmit(value)
    }

    /**
     * Sets the `usersToUploadData` state, which holds the data parsed from the import file,
     * including users, organization IDs, and any errors.
     *
     * @param {object} data - The data object containing user upload information.
     * @param {Array<object>} data.users - An array of user objects parsed from the import file, ready for upload. Each user object should contain properties like `email`, `firstName`, `lastName`, etc.
     * @param {Array<string>} data.organizationIds - An array of organization IDs to which the imported users will be assigned.
     * @param {object|Array<string>} [data.errors] - An object or array containing validation errors encountered during parsing the import file. If present, indicates issues with the data.
     * @param {boolean} [data.isThereTwoSourcesForOrgs] - A boolean flag indicating if the imported data contains conflicting sources for organization assignments (e.g., both 'organizationId' and 'organizationName' columns).
     * @param {boolean} [data.isMissingSourceOfOrgs] - A boolean flag indicating if required organization source data is missing in the imported file.
     * @param {boolean} [data.sendWelcomeMessage] - A boolean flag indicating whether welcome messages should be sent to new users upon successful creation.
     * @param {Array<string>} [data.selectedMessagingTypes] - An array of strings specifying the types of welcome messages to send (e.g., 'email', 'whatsapp'), if `sendWelcomeMessage` is true.
     * @param {boolean} [data.onlyAddOrgsWithoutRemoving] - A boolean flag indicating if only new organization assignments should be added without removing existing ones for users.
     * @returns {void}
     */
    const setUsersToUploadDataFunc = (data) => {
        setUsersToUploadData(data)
    }

    /**
     * Sets the `isRebootUsers` state, which determines if existing users should be "rebooted"
     * (e.g., re-assigned to organizations without removing existing assignments).
     * @param {boolean} value - The boolean value to set `isRebootUsers` to.
     * @returns {void}
     */
    const setIsRebootUsersFunc = (value) => {
        setIsRebootUsers(value)
    }

    /**
     * Determines if the submit button for user import should be disabled.
     * The button is disabled if there are errors in the uploaded data, if an upload is already in progress,
     * if there are no users to upload, or if there are issues with organization source data.
     * @returns {boolean} True if the submit button should be disabled, false otherwise.
     */
    const isSubmitDisabled = () => {
        return !isEmpty(get(usersToUploadData, 'errors')) || get(usersToUploadData, 'isUploading') || !get(usersToUploadData, 'users') || get(usersToUploadData, 'isThereTwoSourcesForOrgs') || get(usersToUploadData, 'isMissingSourceOfOrgs')
    }

    /**
     * Triggers the user upload process by making an API call to create users for organizations.
     * It sets the `isUploading` state to true during the process and back to false upon completion.
     * It also refreshes the user table after a successful upload.
     * @returns {Promise<void>} A promise that resolves when the user creation process is complete.
     */
    const triggerUploadUsers = async () => {
        setIsUploading(true)
        let sendWelcomeMessageData;
        if (get(usersToUploadData, 'sendWelcomeMessage')) {
            sendWelcomeMessageData = {
                messageTypes: get(usersToUploadData, 'selectedMessagingTypes')
            }
        }
        await createUsersForOrganizations({
            organizationIds: get(usersToUploadData, 'organizationIds'),
            users: get(usersToUploadData, 'users'),
            sendWelcomeMessage: sendWelcomeMessageData,
            onlyAddOrgsWithoutRemoving: get(usersToUploadData, 'onlyAddOrgsWithoutRemoving'),
            isRebootUsers
        })
        setIsUploading(false)
        gridContext.triggerTableRefresh()
    }

    return <ImportUsersReactContext.Provider value={{
        canSubmit,
        setCanSubmitFunc,
        usersToUploadData,
        setUsersToUploadDataFunc,
        isSubmitDisabled,
        isUploading,
        triggerUploadUsers,
        setIsRebootUsersFunc
    }}>
        {children}
    </ImportUsersReactContext.Provider>
}
